import { Component } from 'react';
import { Button } from './ui';

/**
 * Stops one broken view from taking down the whole app.
 *
 * React's default behaviour for an uncaught render error is to unmount the
 * entire tree. With no boundary anywhere, a single throw in any one of the ten
 * company tabs blanked the page completely and left it blank — no nav, no error,
 * nothing to click — until a manual reload. That reads as "the browser is in a
 * bad state", which is exactly how it was reported, and it gives no clue which
 * tab caused it.
 *
 * Placed *inside* Layout around the routed view, deliberately, so the sidebar and
 * company switcher survive a bad tab. Recovering then means clicking another tab
 * rather than reloading, and `resetKey` (the current pathname) clears the error
 * automatically on navigation — otherwise a boundary latches and every subsequent
 * tab looks broken too, which is worse than the original bug.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The console is the only place the component stack survives; a production
    // build's minified names make the rendered message alone hard to act on.
    console.error('[view] render failed', error, info?.componentStack);
    this.setState({ info });
  }

  componentDidUpdate(prevProps) {
    // Navigating away must clear the error, or the boundary stays latched and
    // every other tab renders this screen.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, info: null });
    }
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="stack">
        <div className="page-head">
          <div>
            <h1>This view failed to load</h1>
            <p>
              The rest of the app still works — pick another tab from the sidebar. Nothing
              was changed, and no data was lost: this is a display error, not a failed
              write.
            </p>
          </div>
          <div className="page-actions">
            <Button variant="primary" onClick={() => this.setState({ error: null, info: null })}>
              Try again
            </Button>
          </div>
        </div>

        <div className="banner banner-error" role="alert">
          {error.message || String(error)}
          <details className="signin-detail">
            <summary>Technical detail</summary>
            <pre>
              {[error.stack, info?.componentStack].filter(Boolean).join('\n\n') ||
                String(error)}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
